import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'
import { useOverlayContainer } from '@firstbite/ui'
import { cn } from './cn'
import { useModalDepth } from './Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverClose = PopoverPrimitive.Close

interface PopoverContentProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  className?: string
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'center', sideOffset = 4, style, ...props }, ref) => {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )
  // When nested in a react-aria Dialog/Sheet (e.g. the Action Center apply
  // panel), portal into the modal node instead of document.body — otherwise
  // react-aria's focus trap treats this content as "outside the modal" and
  // eats clicks + steals focus, so calendars/menus can't be interacted with
  // (FIR-8102). Returns null outside a react-aria modal → default body portal,
  // so react-bootstrap modals and plain-page popovers are unchanged.
  const overlayContainer = useOverlayContainer()

  return (
    <PopoverPrimitive.Portal container={overlayContainer ?? undefined}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'w-72 rounded-md border border-line bg-surface p-4 text-fg shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        style={{ zIndex, ...style }}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger }
